class UserSettings {
  /**
   *
   * @type {{TIME_24_HOURS: string, TIME_12_HOURS_A: string}}
   */
  static timeMediumFormats = {
    TIME_24_HOURS: 'HH:mm',
    TIME_12_HOURS_A: 'hh:mm a'
  };

  /**
   *
   * @type {{TIME_24_HOURS: string, TIME_12_HOURS_A: string}}
   */
  static timeLongFormats = {
    TIME_24_HOURS: 'HH:mm:ss',
    TIME_12_HOURS_A: 'hh:mm:ss a'
  };

  /**
   *
   * @type {{TIME_24_HOURS: string, TIME_12_HOURS_A: string}}
   */
  static timeLongWithMsFormats = {
    TIME_24_HOURS: 'HH:mm:ss.SSS',
    TIME_12_HOURS_A: 'hh:mm:ss.SSS a'
  };

  /**
   *
   * @type {{TIME_24_HOURS: string, TIME_12_HOURS_A: string}}
   */
  static timeShortFormats = {
    TIME_24_HOURS: 'H:mm',
    TIME_12_HOURS_A: 'h:mm a'
  };

  /**
   *
   * @type {{DATE_L_PERIOD: string, DATE_B_SLASH: string, DATE_L_SLASH: string, DATE_M_DASH: string}}
   */
  static dateLongFormats = {
    DATE_L_PERIOD: 'dd. DD.MM.YYYY',
    DATE_L_SLASH: 'dd. DD/MM/YYYY',
    DATE_B_SLASH: 'dd. MM/DD/YYYY',
    DATE_M_DASH: 'YYYY-MM-DD'
  };

  /**
   *
   * @type {{DATE_L_PERIOD: string, DATE_B_SLASH: string, DATE_L_SLASH: string, DATE_M_DASH: string}}
   */
  static dateMediumFormats = {
    DATE_L_PERIOD: 'DD.MM.YYYY',
    DATE_L_SLASH: 'DD/MM/YYYY',
    DATE_B_SLASH: 'MM/DD/YYYY',
    DATE_M_DASH: 'YYYY-MM-DD'
  };

  /**
   *
   * @type {{LENGTH_IMPERIAL: number, LENGTH_METRIC: number}}
   */
  static lengthFormats = {
    LENGTH_METRIC: 1,
    LENGTH_IMPERIAL: 0.621371192237334
  };

  /**
   *
   * @type {{DE: string, EN: string}}
   */
  static numberFormats = {
    DE: 'DE',
    EN: 'EN'
  };

  /**
   *
   * @type {{LENGTH_IMPERIAL: string, LENGTH_METRIC: string}}
   */
  static lengthUnits = {
    LENGTH_METRIC: 'km',
    LENGTH_IMPERIAL: 'mi'
  };

  /**
   *
   * @type {{WEIGHT_IMPERIAL: number, WEIGHT_METRIC: number}}
   */
  static weightFormats = {
    WEIGHT_METRIC: 1,
    WEIGHT_IMPERIAL: 2.20462
  };

  /**
   *
   * @type {{WEIGHT_IMPERIAL: string, WEIGHT_METRIC: string}}
   */
  static weightUnits = {
    WEIGHT_METRIC: 'kg',
    WEIGHT_IMPERIAL: 'lb'
  };

  filters = {};

  userDateFormat;

  userLengthFormat;

  userNumberFormat;

  userTimeFormat;

  userWeightFormat;

  /**
   *
   * @param userSettings
   */
  constructor(userSettings) {
    this.setUserSettings(userSettings);
  }

  setUserSettings = (userSettings) => {
    this.userDateFormat = userSettings?.dateFormat || null;
    this.userLengthFormat = userSettings?.lengthFormat || 'LENGTH_METRIC';
    this.userNumberFormat = userSettings?.numberFormat || 'EN';
    this.userTimeFormat = userSettings?.timeFormat || null;
    this.userWeightFormat = userSettings?.weightFormat || 'WEIGHT_METRIC';
    this.setupAllFilters();
  };

  setupAllFilters = () => {
    this.configureDateFilters();
    this.configureLengthFilters();
    this.configureNumberFilters();
    this.configureTimeFilters();
    this.configureWeightFilters();
  };

  setupFilter = (filterConfig) => {
    this.filters[filterConfig.name] = filterConfig.method;
  };

  configureDateFilters() {
    this.setupFilter({
      method: () => {
        return UserSettings.dateLongFormats[this.userDateFormat];
      },
      name: 'applyDateLongFormat'
    });

    this.setupFilter({
      method: () => {
        return UserSettings.dateMediumFormats[this.userDateFormat];
      },
      name: 'applyDateMediumFormat'
    });
  }

  configureLengthFilters() {
    this.setupFilter({
      method: (meters) => {
        if (meters) {
          return (
            Math.round(
              meters * UserSettings.lengthFormats[this.userLengthFormat] * 100
            ) / 100
          );
        }
        return 0;
      },
      name: 'applyLengthFormat'
    });

    this.setupFilter({
      method: () => {
        return this.userLengthFormat
          ? UserSettings.lengthUnits[this.userLengthFormat]
          : UserSettings.lengthUnits[UserSettings.lengthFormats.LENGTH_METRIC];
      },
      name: 'applyLengthUnit'
    });
  }

  configureNumberFilters() {
    this.setupFilter({
      method: () => {
        return UserSettings.numberFormats[this.userNumberFormat];
      },
      name: 'applyNumberFormat'
    });
  }

  configureTimeFilters() {
    this.setupFilter({
      method: () => {
        return UserSettings.timeLongFormats[this.userTimeFormat];
      },
      name: 'applyTimeLongFormat'
    });

    this.setupFilter({
      method: () => {
        return UserSettings.timeLongWithMsFormats[this.userTimeFormat];
      },
      name: 'applyTimeLongWithMsFormat'
    });

    this.setupFilter({
      method: () => {
        return UserSettings.timeMediumFormats[this.userTimeFormat];
      },
      name: 'applyTimeMediumFormat'
    });

    this.setupFilter({
      method: () => {
        return UserSettings.timeShortFormats[this.userTimeFormat];
      },
      name: 'applyTimeShortFormat'
    });
  }

  configureWeightFilters() {
    this.setupFilter({
      method: (kilograms) => {
        if (kilograms) {
          return (
            Math.round(
              kilograms *
                UserSettings.weightFormats[this.userWeightFormat] *
                100
            ) / 100
          );
        }
        return 0;
      },
      name: 'applyWeightFormat'
    });

    this.setupFilter({
      method: () => {
        return this.userWeightFormat
          ? UserSettings.weightUnits[this.userWeightFormat]
          : UserSettings.weightUnits[UserSettings.weightFormats.WEIGHT_METRIC];
      },
      name: 'applyWeightUnit'
    });
  }

  get dateLongFormat() {
    return this.filters.applyDateLongFormat();
  }

  get dateMediumFormat() {
    return this.filters.applyDateMediumFormat();
  }

  get numberFormat() {
    return this.filters.applyNumberFormat();
  }

  get timeLongFormat() {
    return this.filters.applyTimeLongFormat();
  }

  get timeLongWithMsFormat() {
    return this.filters.applyTimeLongWithMsFormat();
  }

  get timeMediumFormat() {
    return this.filters.applyTimeMediumFormat();
  }

  get timeShortFormat() {
    return this.filters.applyTimeShortFormat();
  }

  lengthFormat(val) {
    return this.filters.applyLengthFormat(val);
  }

  get lengthUnit() {
    return this.filters.applyLengthUnit();
  }

  weightFormat(val) {
    return this.filters.applyWeightFormat(val);
  }

  get weightUnit() {
    return this.filters.applyWeightUnit();
  }

  updateUserSettings(settings) {
    this.setUserSettings(settings);
    return this;
  }

  /**
   * Returns the passed value with the applied numberFormat
   * @param value
   * @returns {string}
   */
  applyDecimalPointer(value) {
    return new Intl.NumberFormat(this.numberFormat).format(value);
  }
}

export default UserSettings;
